import {useEffect, useMemo} from 'react'
import {useLenis} from 'lenis/react'

/**
 * Pauses and resumes Lenis smooth scrolling while an overlay (drawer, modal,
 * navigation flyout) is open.
 *
 * This is a Lenis-only lock: it does NOT lock native body scroll. Every current
 * consumer is a Chakra overlay (v3 `Dialog`, v2 `Modal` / `Drawer`) that already
 * locks native body scroll itself; this hook only stops Lenis's independent
 * smooth-scroll engine from scrolling the page underneath the overlay. Surfaces
 * that are NOT Chakra overlays (the Xleap navigation header) additionally need
 * `useNativeScrollLock` to lock the body. On routes without a `LenisProvider`
 * this hook is a no-op (see `app/contexts/lenis.jsx`).
 *
 * @param {boolean} [lock] - whether Lenis scrolling should be paused
 * @returns {{lock: function(): void, unlock: function(): void}}
 * @example
 * useLenisScrollLock(isOpen)
 */
function useLenisScrollLock(lock) {
    const lenis = useLenis()

    useEffect(() => {
        if (lenis) {
            if (lock) lenis.stop()
            else lenis.start()
        }
    }, [lenis, lock])

    return useMemo(
        () => ({
            lock: () => lenis?.stop(),
            unlock: () => lenis?.start()
        }),
        [lenis]
    )
}

export default useLenisScrollLock
